import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box, Button, Chip, Divider, IconButton, Paper, Tooltip, Typography,
  makeStyles, useMediaQuery, useTheme
} from '@material-ui/core'
import { useRecoilValue } from 'recoil'
import AddIcon from '@material-ui/icons/Add'
import RefreshIcon from '@material-ui/icons/Refresh'
import NotificationsIcon from '@material-ui/icons/Notifications'
import MessageThread from './MessageThread'
import NewConversationDialog from './NewConversationDialog'
import api from '../api/client'
import { enablePush, disablePush, isSubscribed, pushSupported, sendTest } from '../push'
import { gregorianToJalali, toFarsiDigits } from '../assets/utils'
import { userInfoState } from '../recoil/atoms'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    height: 'calc(100vh - 130px)',
    minHeight: 480,
  },
  side: {
    width: 330,
    flexShrink: 0,
    background: '#fff',
    border: '1px solid #e3e6f2',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sideHead: {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #eef0f8',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  sideTitle: { fontWeight: 800, color: '#1a237e', fontSize: 14.5 },
  sideList: { flex: 1, overflowY: 'auto' },
  convItem: {
    padding: '10px 12px',
    borderBottom: '1px solid #f2f4fa',
    cursor: 'pointer',
    '&:hover': { background: '#f7f8fc' },
  },
  convItemOn: { background: '#eef1ff', borderRight: '3px solid #1a237e' },
  convTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  convWho: { fontWeight: 700, fontSize: 13, color: '#1a237e' },
  convMeta: { fontSize: 11, color: '#7a80a3', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' },
  convSnippet: { fontSize: 12, color: '#5a6b8c', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unreadDot: {
    background: '#ff5252', color: '#fff', borderRadius: 999,
    fontSize: 10.5, fontWeight: 800, padding: '1px 7px', flexShrink: 0,
  },
  roleChip: { fontSize: 10, fontWeight: 700, height: 19 },
  tag: { fontSize: 10, fontWeight: 700, height: 19, background: '#eceff1', color: '#455a64' },
  main: {
    flex: 1, minWidth: 0,
    background: '#fff',
    border: '1px solid #e3e6f2',
    borderRadius: 12,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  mainHead: {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #eef0f8',
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  mainTitle: { fontWeight: 800, color: '#1a237e', fontSize: 14.5 },
  mainBody: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: theme.spacing(1.5) },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', color: '#8a92b2', gap: 10, textAlign: 'center', padding: 20,
  },
  emptyIcon: { fontSize: 44 },
  // موبایل
  mobileSide: { width: '100%' },
  mobileMain: { display: 'none' },
  mobileMainOn: { display: 'flex', width: '100%' },
  backBtn: { display: 'none' },
  backBtnOn: { display: 'inline-flex' },
}))

const ROLE_COLORS = {
  Admin: { bg: '#e8eafd', fg: '#1a237e' },
  Department: { bg: '#e3f2fd', fg: '#1565c0' },
  DepartmentManager: { bg: '#e0f2f1', fg: '#00796b' },
  Contractor: { bg: '#f3e5f5', fg: '#7b1fa2' },
  Consultant: { bg: '#fff3e0', fg: '#ef6c00' },
  Supervisor: { bg: '#e0f7fa', fg: '#00838f' },
}

/** «مرکز پیام» — چیدمانِ دوسطونی مثلِ مرکز پیامِ ارجان فیش */
const MessageCenter = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const userInfo = useRecoilValue(userInfoState)
  const currentRole = userInfo?.role || 'Contractor'

  const [sp] = useSearchParams()
  const wantPartner = sp.get('to') ? parseInt(sp.get('to'), 10) : null
  const [conversations, setConversations] = useState([])
  const [active, setActive] = useState(null)
  const [newOpen, setNewOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [oversightOn, setOversightOn] = useState(false)

  const isAdmin = userInfo?.role === 'Admin'
  const load = (oversight) => {
    const on = oversight === undefined ? oversightOn : oversight
    setOversightOn(on)
    const url = on && isAdmin
      ? `messages/admin/oversight/?actor=${userInfo.id}`
      : 'messages/conversations/'
    api.get(url).then((res) => {
      if (res.status === 200) {
        const rows = res.data || []
        setConversations(on && isAdmin
          ? rows.map((r) => ({
              ...r,
              other_user_id: r.sender_id === userInfo.id ? r.receiver_id : r.sender_id,
              other_user_name: r.sender_id === userInfo.id ? r.receiver_name : r.sender_name,
            }))
          : rows)
      }
    }).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const activeConv = useMemo(
    () => conversations.find((c) =>
      c.other_user_id === active?.other_user_id &&
      (c.contract_id || null) === (active?.contract_id || null) &&
      c.subject === active?.subject
    ) || null,
    [conversations, active]
  )

  const openConversation = (c) => {
    setActive({
      other_user_id: c.other_user_id,
      other_user_name: c.other_user_name,
      is_subscribed: c.is_subscribed,
      other_user_role: c.other_user_role,
      other_user_role_label: c.other_user_role_label,
      contract_id: c.contract_id,
      contract_number: c.contract_number,
      contract_organization: c.contract_organization,
      subject: c.subject,
    })
  }

  const handleCreated = (info) => {
    load()
    setActive({
      other_user_id: info.partnerId,
      other_user_name: info.partnerName,
      contract_id: info.contractId,
      subject: info.subject,
    })
  }

  // نشان وضعیت اشتراک پیمانکار — فقط در پنل ادمین (سرور فقط برای ادمین می‌فرستد)
  const subChip = (v) => {
    if (v === true) {
      return (
        <span style={{
          background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7',
          borderRadius: 999, padding: '1px 9px', fontSize: 10.5, fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>اشتراک فعال</span>
      )
    }
    if (v === false) {
      return (
        <span style={{
          background: '#fbe9e7', color: '#c62828', border: '1px solid #ffab91',
          borderRadius: 999, padding: '1px 9px', fontSize: 10.5, fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>بدون اشتراک</span>
      )
    }
    return null
  }

  const roleChip = (role, label) => (
    <Chip
      className={classes.roleChip}
      label={label || role || ''}
      style={{ background: (ROLE_COLORS[role] || {}).bg, color: (ROLE_COLORS[role] || {}).fg }}
    />
  )

  // ── اعلانِ مرورگر (Web Push) ──
  const [pushOn, setPushOn] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)
  useEffect(() => {
    let alive = true
    if (pushSupported()) isSubscribed().then((v) => { if (alive) setPushOn(v) })
    return () => { alive = false }
  }, [])
  const togglePush = async () => {
    setPushBusy(true)
    const r = pushOn ? await disablePush() : await enablePush()
    setPushBusy(false)
    if (r.ok) {
      setPushOn(!pushOn)
      if (!pushOn) {
        const t = await sendTest()
        if (t.ok && t.sent === 0) {
          // اشتراک ثبت شد ولی ارسال نشد؛ کاربر را گیج نکنیم
          // (مثلاً اعلان‌ها روی سرور فعال نیست)
        }
      }
    } else if (r.error) {
      alert(r.error)
    }
  }

  // از فهرست‌ها با ?to=<userId> آمده‌ایم ⇒ همان مخاطب انتخاب شود
  useEffect(() => {
    if (!wantPartner || !conversations.length) return
    const conv = conversations.find((c) => c.other_user_id === wantPartner)
    if (conv) {
      setActive(conv)
    } else {
      setNewOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantPartner, conversations.length])

  const listPane = (
    <Paper className={`${classes.side} ${isMobile ? classes.mobileSide : ''}`} elevation={0} data-tour="message-conversations">
      <div className={classes.sideHead} data-tour="message-new">
        <Typography className={classes.sideTitle}>گفتگوها</Typography>
        <Box display="flex" style={{ gap: 4 }}>
          <Tooltip title={pushOn ? 'اعلان مرورگر فعال است — برای لغو کلیک کنید' : 'فعال‌سازی اعلان مرورگر'}>
            <IconButton size="small" onClick={togglePush} disabled={pushBusy}
              style={{ color: pushOn ? '#2e7d32' : '#7b86a3' }}>
              <NotificationsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="به‌روزرسانی">
            <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setNewOpen(true)}>
            گفتگوی جدید
          </Button>
        </Box>
      </div>
      <Divider />
      <div className={classes.sideList}>
        {loading && <Box style={{ padding: 16 }}><Typography color="textSecondary">در حال بارگذاری…</Typography></Box>}
        {!loading && !conversations.length && (
          <Box style={{ padding: 24, textAlign: 'center' }}>
            <Typography style={{ fontSize: 34 }}>✉️</Typography>
            <Typography color="textSecondary">هنوز گفتگویی ندارید</Typography>
          </Box>
        )}
        {isAdmin && !oversightOn && (
          <div
            onClick={() => { setActive(null); load(true) }}
            style={{
              margin: '0 0 8px', padding: '9px 12px', borderRadius: 10, cursor: 'pointer',
              border: '1px solid #c5cae9', background: '#eef1fb', color: '#1a237e',
              fontWeight: 700, fontSize: 12.5, textAlign: 'center',
            }}
          >
            👁 مشاهدهٔ همهٔ پیام‌های سامانه
          </div>
        )}
        {isAdmin && oversightOn && (
          <div style={{
            margin: '0 0 8px', padding: '9px 12px', borderRadius: 10,
            border: '1px solid #ffe082', background: '#fff8e1', color: '#8d6e63',
            fontSize: 11.5, lineHeight: 1.9,
          }}>
            <div style={{ fontWeight: 800, color: '#e65100' }}>👁 حالت نظارت فعال است</div>
            شما همهٔ گفتگوهای سامانه را می‌بینید؛ این مشاهده «زمان رویت» ثبت نمی‌کند.
            <div
              onClick={() => { setActive(null); load(false) }}
              style={{ marginTop: 6, cursor: 'pointer', fontWeight: 800, color: '#1a237e' }}
            >
              ↩ بازگشت به پیام‌های من
            </div>
          </div>
        )}
        {conversations.map((c, i) => {
          const on = active && active.other_user_id === c.other_user_id
            && (active.contract_id || null) === (c.contract_id || null)
            && active.subject === c.subject
          return (
            <div
              key={`${c.contract_id || 0}-${c.subject}-${c.other_user_id}-${i}`}
              className={`${classes.convItem} ${on ? classes.convItemOn : ''}`}
              onClick={() => openConversation(c)}
            >
              <div className={classes.convTop}>
                <span className={classes.convWho}>
                  {toFarsiDigits(c.other_user_name)} {roleChip(c.other_user_role, c.other_user_role_label)}
                </span>
                {c.unread_count > 0
                  ? <span className={classes.unreadDot}>{toFarsiDigits(c.unread_count)}</span>
                  : <Typography style={{ fontSize: 10.5, color: '#9aa1c2', flexShrink: 0 }}>
                      {gregorianToJalali(c.last_message_datetime)}
                    </Typography>}
              </div>
              <div className={classes.convMeta}>
                <Chip
                  className={classes.tag}
                  label={c.has_contract ? `قرارداد ${toFarsiDigits(c.contract_number || '-')}` : 'بدون قرارداد'}
                />
                {subChip(c.is_subscribed)}
                {c.is_subscribed === true && (
                  <Chip size="small" label="اشتراک فعال"
                    style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 10.5, height: 20 }} />
                )}
                {c.is_subscribed === false && (
                  <Chip size="small" label="بدون اشتراک"
                    style={{ background: '#fbe9e7', color: '#c62828', fontWeight: 700, fontSize: 10.5, height: 20 }} />
                )}
                <span>{toFarsiDigits(c.subject || 'پیام')}</span>
              </div>
              <div className={classes.convSnippet}>
                {c.unread_count > 0 ? '🔒 پیام خوانده‌نشده — برای مشاهده باز کنید' : toFarsiDigits(c.last_message || '')}
              </div>
            </div>
          )
        })}
      </div>
    </Paper>
  )

  const threadPane = (
    <Paper className={`${classes.main} ${isMobile ? (active ? classes.mobileMainOn : classes.mobileMain) : ''}`} elevation={0}>
      {active ? (
        <>
          <div className={classes.mainHead}>
            {isMobile && (
              <IconButton size="small" onClick={() => setActive(null)}>
                <RefreshIcon fontSize="small" style={{ transform: 'rotate(90deg)' }} />
              </IconButton>
            )}
            <Typography className={classes.mainTitle}>
              {toFarsiDigits(active.other_user_name || '')} {subChip(active.is_subscribed ?? (activeConv && activeConv.is_subscribed))}
            </Typography>
            {roleChip(active.other_user_role, active.other_user_role_label)}
            <Chip
              className={classes.tag}
              label={active.contract_id
                ? `قرارداد ${toFarsiDigits(active.contract_number || '-')}${active.contract_organization ? ' · ' + toFarsiDigits(active.contract_organization) : ''}`
                : 'بدون قرارداد'}
            />
            <Typography style={{ fontSize: 12.5, color: '#5a6b8c' }}>
              موضوع: {toFarsiDigits(active.subject || 'پیام')}
            </Typography>
          </div>
          <Divider />
          <div className={classes.mainBody}>
            <MessageThread
              contractId={active.contract_id || null}
              partnerId={active.other_user_id}
              subject={active.subject}
              viewAsUser={oversightOn && isAdmin ? userInfo.id : null}
              onNewMessage={() => load()}
            />
          </div>
        </>
      ) : (
        <div className={classes.empty}>
          <span className={classes.emptyIcon}>💬</span>
          <Typography variant="h6">گفتگویی انتخاب نشده است</Typography>
          <Typography variant="body2">
            از فهرستِ کنار یک گفتگو را انتخاب کنید یا با «گفتگوی جدید» یکی بسازید.
          </Typography>
        </div>
      )}
    </Paper>
  )

  return (
    <>
      <div className={classes.root}>
        {listPane}
        {threadPane}
      </div>

      <NewConversationDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={handleCreated}
        currentRole={currentRole}
      />
    </>
  )
}

export default MessageCenter
